// src/app/App.tsx
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// import { useAuth } from './hooks/useAuth';
import { SearchProvider } from './context/SearchContext';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AuthLayout from './layouts/AuthLayout';
import CustomerLayout from './layouts/CustomerLayout';
import ProviderLayout from './layouts/ProviderLayout';
import AdminLayout from './layouts/AdminLayout';

// Protected Route Component
import { ProtectedRoute } from './components/common/ProtectedRoute';

// ============================================
// CUSTOMER PAGES
// ============================================
const CustomerDashboard = lazy(() => import('./features/customer/pages/Dashboard'));
const MyBookings = lazy(() => import('./features/customer/pages/MyBookings'));
const MyFavorites = lazy(() => import('./features/customer/pages/MyFavorites'));
const MyProfile = lazy(() => import('./features/customer/pages/MyProfile'));
const CustomerNotifications = lazy(() => import('./features/customer/pages/Notifications'));
const CustomerSupport = lazy(() => import('./features/customer/pages/Support'));
//const CustomerBookingDetails = lazy(() => import('./features/customer/pages/BookingDetails'));

// ============================================
// PROVIDER PAGES
// ============================================
const ProviderDashboard = lazy(() => import('./features/provider/pages/Dashboard'));
const ProviderProfile = lazy(() => import('./features/provider/pages/Profile'));
const ProviderServices = lazy(() => import('./features/provider/pages/ProviderServices'));
const AddService = lazy(() => import('./features/provider/pages/AddService'));
const EditService = lazy(() => import('./features/provider/pages/EditService'));
const ProviderBookings = lazy(() => import('./features/provider/pages/ManageBookings'));
const ProviderBookingDetails = lazy(() => import('./features/provider/pages/BookingDetails'));
const ProviderAnalytics = lazy(() => import('./features/provider/pages/Analytics'));
const ProviderReviews = lazy(() => import('./features/provider/pages/Reviews'));
const ProviderSubscription = lazy(() => import('./features/provider/pages/Subscription'));
const ProviderNotifications = lazy(() => import('./features/provider/pages/Notifications'));
const ProviderSupport = lazy(() => import('./features/provider/pages/Support'));

// ============================================
// ADMIN PAGES
// ============================================
const AdminDashboard = lazy(() => import('./features/admin/pages/Dashboard'));
const AdminUsers = lazy(() => import('./features/admin/pages/ManageUsers'));
const AdminProviders = lazy(() => import('./features/admin/pages/ManageProviders'));
const AdminCategories = lazy(() => import('./features/admin/pages/ManageCategories'));
const AdminBookings = lazy(() => import('./features/admin/pages/ManageBookings'));
//const AdminBookingDetails = lazy(() => import('./features/admin/pages/BookingDetails'));
const AdminPayments = lazy(() => import('./features/admin/pages/ManagePayments'));
const AdminAnalytics = lazy(() => import('./features/admin/pages/Analytics'));
const AdminSupport = lazy(() => import('./features/admin/pages/SupportTickets'));
const AdminSettings = lazy(() => import('./features/admin/pages/SystemSettings'));
const AddCategory= lazy(()=>import('./features/admin/pages/AddCategory'));
const AdminPaymentDetails = lazy(() => import('./features/admin/pages/PaymentDetails'));
const LiveChat=lazy(()=>import('./features/admin/pages/LiveChat'));
const AdminProviderDetails=lazy(()=>import('./features/admin/pages/ProviderDetails'));
// ============================================
// PUBLIC PAGES
// ============================================
const Home = lazy(() => import('./app/pages/Home'));
const SearchProviders = lazy(() => import('./features/customer/pages/SearchProviders'));
const ProviderDetails = lazy(() => import('./features/customer/pages/ProviderDetails'));

// ============================================
// AUTH PAGES
// ============================================
const Login = lazy(() => import('./features/authentication/pages/Login'));
const Register = lazy(() => import('./features/authentication/pages/Register'));
const ForgotPassword = lazy(() => import('./features/authentication/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./features/authentication/pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./features/authentication/pages/VerifyEmail'));

// ============================================
// LOADING COMPONENT
// ============================================
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

// ============================================
// ROUTE WRAPPER
// ============================================
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
);

// ============================================
// MAIN APP
// ============================================
function App() {
  // const { user } = useAuth();

  // // Role-based redirect helper
  // const getRoleDashboard = () => {
  //   if (!user) return '/';
  //   switch (user.role) {
  //     case 'customer':
  //       return '/customer/dashboard';
  //     case 'provider':
  //       return '/provider/dashboard';
  //     case 'admin':
  //       return '/admin/dashboard';
  //     default:
  //       return '/';
  //   }
  // };

  return (
    <SearchProvider>
      <>
        <Helmet defaultTitle="Jibu24 - 24-Hour Service Discovery" titleTemplate="%s | Jibu24">
          <meta name="description" content="Find verified 24-hour service providers near you" />
          <meta name="theme-color" content="#22c55e" />
          <link rel="icon" href="/favicon.ico" />
        </Helmet>

        <Routes>
          {/* ============================================ */}
          {/* PUBLIC ROUTES */}
          {/* ============================================ */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<LazyRoute><Home /></LazyRoute>} />
            <Route path="search" element={<LazyRoute><SearchProviders /></LazyRoute>} />
            <Route path="provider/:id" element={<LazyRoute><ProviderDetails /></LazyRoute>} />
            <Route path="about" element={<div>About Page</div>} />
            <Route path="contact" element={<div>Contact Page</div>} />
            <Route path="terms" element={<div>Terms & Conditions</div>} />
            <Route path="privacy" element={<div>Privacy Policy</div>} />
            <Route path="faq" element={<div>FAQ</div>} />
          </Route>

          {/* ============================================ */}
          {/* AUTH ROUTES */}
          {/* ============================================ */}
          <Route path="/auth" element={<AuthLayout />}>
            <Route path="login" element={<LazyRoute><Login /></LazyRoute>} />
            <Route path="register" element={<LazyRoute><Register /></LazyRoute>} />
            <Route path="forgot-password" element={<LazyRoute><ForgotPassword /></LazyRoute>} />
            <Route path="reset-password" element={<LazyRoute><ResetPassword /></LazyRoute>} />
            <Route path="verify-email" element={<LazyRoute><VerifyEmail /></LazyRoute>} />
          </Route>

          {/* ============================================ */}
          {/* CUSTOMER ROUTES */}
          {/* ============================================ */}
          <Route
            path="/customer"
            element={
              <ProtectedRoute requiredRole="customer">
                <CustomerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/customer/dashboard" replace />} />
            <Route path="dashboard" element={<LazyRoute><CustomerDashboard /></LazyRoute>} />
            <Route path="bookings" element={<LazyRoute><MyBookings /></LazyRoute>} />
            {/* <Route path="bookings/:id" element={<LazyRoute><CustomerBookingDetails /></LazyRoute>} /> */}
            <Route path="favorites" element={<LazyRoute><MyFavorites /></LazyRoute>} />
            <Route path="notifications" element={<LazyRoute><CustomerNotifications /></LazyRoute>} />
            <Route path="profile" element={<LazyRoute><MyProfile /></LazyRoute>} />
            <Route path="settings" element={<LazyRoute><div>Settings</div></LazyRoute>} />
            <Route path="support" element={<LazyRoute><CustomerSupport /></LazyRoute>} />
            <Route path="reviews" element={<LazyRoute><div>My Reviews</div></LazyRoute>} />
          </Route>

          {/* ============================================ */}
          {/* PROVIDER ROUTES */}
          {/* ============================================ */}
          <Route
            path="/provider"
            element={
              <ProtectedRoute requiredRole="provider">
                <ProviderLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/provider/dashboard" replace />} />
            
            {/* Dashboard */}
            <Route path="dashboard" element={<LazyRoute><ProviderDashboard /></LazyRoute>} />
            
            {/* Profile */}
            <Route path="profile" element={<LazyRoute><ProviderProfile /></LazyRoute>} />
            
            {/* Services */}
            <Route path="services" element={<LazyRoute><ProviderServices /></LazyRoute>} />
            <Route path="services/add" element={<LazyRoute><AddService /></LazyRoute>} />
            <Route path="services/:id" element={<LazyRoute><EditService /></LazyRoute>} />
            <Route path="services/categories" element={<LazyRoute><div>Service Categories</div></LazyRoute>} />
            
            {/* Bookings - Main & Sub-routes */}
            <Route path="bookings" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/pending" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/confirmed" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/in-progress" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/completed" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/cancelled" element={<LazyRoute><ProviderBookings /></LazyRoute>} />
            <Route path="bookings/:id" element={<LazyRoute><ProviderBookingDetails /></LazyRoute>} />
            
            {/* Analytics */}
            <Route path="analytics" element={<LazyRoute><ProviderAnalytics /></LazyRoute>} />
            <Route path="analytics/revenue" element={<LazyRoute><div>Revenue Analytics</div></LazyRoute>} />
            <Route path="analytics/performance" element={<LazyRoute><div>Performance Analytics</div></LazyRoute>} />
            <Route path="analytics/customers" element={<LazyRoute><div>Customer Insights</div></LazyRoute>} />
            
            {/* Reviews */}
            <Route path="reviews" element={<LazyRoute><ProviderReviews /></LazyRoute>} />
            <Route path="reviews/pending" element={<LazyRoute><div>Pending Reviews</div></LazyRoute>} />
            
            {/* Subscription */}
            <Route path="subscription" element={<LazyRoute><ProviderSubscription /></LazyRoute>} />
            
            {/* Notifications */}
            <Route path="notifications" element={<LazyRoute><ProviderNotifications /></LazyRoute>} />
            
            {/* Support */}
            <Route path="support" element={<LazyRoute><ProviderSupport /></LazyRoute>} />
          </Route>

          {/* ============================================ */}
          {/* ADMIN ROUTES */}
          {/* ============================================ */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<LazyRoute><AdminDashboard /></LazyRoute>} />
            
            {/* Users */}
            <Route path="providers/:id" element={<AdminProviderDetails/>} />
            <Route path="users" element={<LazyRoute><AdminUsers /></LazyRoute>} />
            <Route path="users/:id" element={<LazyRoute><div>User Details</div></LazyRoute>} />
            <Route path="users/customers" element={<LazyRoute><div>Customers</div></LazyRoute>} />
            <Route path="users/providers" element={<LazyRoute><div>Providers</div></LazyRoute>} />
            <Route path="users/admins" element={<LazyRoute><div>Admins</div></LazyRoute>} />
                <Route path="livechat" element={<LazyRoute><LiveChat/></LazyRoute>} />
            
            {/* Providers */}
            <Route path="providers" element={<LazyRoute><AdminProviders /></LazyRoute>} />
            <Route path="providers/:id" element={<LazyRoute><div>Provider Details</div></LazyRoute>} />
            <Route path="providers/pending" element={<LazyRoute><div>Pending Verification</div></LazyRoute>} />
            <Route path="providers/verified" element={<LazyRoute><div>Verified Providers</div></LazyRoute>} />
            <Route path="providers/suspended" element={<LazyRoute><div>Suspended Providers</div></LazyRoute>} />
            
            {/* Categories */}
            <Route path="categories" element={<LazyRoute><AdminCategories /></LazyRoute>} />
            <Route path="categories/add" element={<LazyRoute><AddCategory /></LazyRoute>} />
            
            <Route path="categories/:id" element={<LazyRoute><div>Edit Category</div></LazyRoute>} />
            
            {/* Services */}
            <Route path="services" element={<LazyRoute><div>Manage Services</div></LazyRoute>} />
            <Route path="services/add" element={<LazyRoute><div>Add Service</div></LazyRoute>} />
            <Route path="services/:id" element={<LazyRoute><div>Edit Service</div></LazyRoute>} />
            
            {/* Bookings */}
            <Route path="bookings" element={<LazyRoute><AdminBookings /></LazyRoute>} />
            {/* <Route path="bookings/:id" element={<LazyRoute><AdminBookingDetails /></LazyRoute>} /> */}
            <Route path="bookings/pending" element={<LazyRoute><div>Pending Bookings</div></LazyRoute>} />
            <Route path="bookings/in-progress" element={<LazyRoute><div>In Progress Bookings</div></LazyRoute>} />
            <Route path="bookings/completed" element={<LazyRoute><div>Completed Bookings</div></LazyRoute>} />
            <Route path="bookings/cancelled" element={<LazyRoute><div>Cancelled Bookings</div></LazyRoute>} />
            
            {/* Payments */}
            <Route path="payments" element={<LazyRoute><AdminPayments /></LazyRoute>} />
            <Route path="payments/:id" element={<LazyRoute><AdminPaymentDetails/></LazyRoute>} />
            <Route path="payments/pending" element={<LazyRoute><div>Pending Payments</div></LazyRoute>} />
            <Route path="payments/failed" element={<LazyRoute><div>Failed Payments</div></LazyRoute>} />
            
            {/* Analytics */}
            <Route path="analytics" element={<LazyRoute><AdminAnalytics /></LazyRoute>} />
            <Route path="analytics/reports" element={<LazyRoute><div>Reports</div></LazyRoute>} />
            <Route path="analytics/revenue" element={<LazyRoute><div>Revenue Analytics</div></LazyRoute>} />
            <Route path="analytics/users" element={<LazyRoute><div>User Analytics</div></LazyRoute>} />
            
            {/* Support */}
            <Route path="support" element={<LazyRoute><AdminSupport /></LazyRoute>} />
            <Route path="support/:id" element={<LazyRoute><div>Ticket Details</div></LazyRoute>} />
            <Route path="support/open" element={<LazyRoute><div>Open Tickets</div></LazyRoute>} />
            <Route path="support/in-progress" element={<LazyRoute><div>In Progress Tickets</div></LazyRoute>} />
            <Route path="support/resolved" element={<LazyRoute><div>Resolved Tickets</div></LazyRoute>} />
            
            {/* Settings */}
            <Route path="settings" element={<LazyRoute><AdminSettings /></LazyRoute>} />
            <Route path="settings/general" element={<LazyRoute><AdminSettings/></LazyRoute>} />
            <Route path="settings/security" element={<LazyRoute><div>Security Settings</div></LazyRoute>} />
            <Route path="settings/notifications" element={<LazyRoute><div>Notification Settings</div></LazyRoute>} />
            <Route path="settings/payments" element={<LazyRoute><div>Payment Settings</div></LazyRoute>} />
          </Route>

          {/* ============================================ */}
          {/* 404 - NOT FOUND */}
          {/* ============================================ */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    </SearchProvider>
  );
}

export default App;